import { Component, HostListener, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { WithdrawalService, WithdrawalItem } from '../../../service/withdrawal.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { PrimengImports } from '../../../shared/primeng-imports';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
type Status = WithdrawalItem['status'];

@Component({
  selector: 'app-withdrawal-management-tab',
  imports: [CommonModule, FormsModule, PrimengImports, LucideIconsModule, DropdownModule],
  templateUrl: './withdrawal-management-tab.component.html',
  styleUrl: './withdrawal-management-tab.component.css',
    providers: [MessageService, ConfirmationService]

})
export class WithdrawalManagementTabComponent  implements OnInit, OnDestroy {
 private api = inject(WithdrawalService);
  private toast = inject(MessageService);
  private confirm = inject(ConfirmationService);

  // Filters & paging
  statusFilter: '' | Status = '';
  page = 1;
  limit = 20;
  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  loading = signal(false);
  rows = signal<WithdrawalItem[]>([]);
  total = signal(0);

  // action dialog
  showActionDialog = signal(false);
  actionType = signal<'initiate'|'process'|'complete'|'reject'|null>(null);
  actionRef = signal<string>('');
  actionNotes = signal<string>('');
  activeRow = signal<WithdrawalItem | null>(null);

  statusOptions: { label: string; value: '' | Status }[] = [
    { label: 'All', value: '' },
    { label: 'Requested', value: 'Requested' },
    { label: 'Initiated', value: 'Initiated' },
    { label: 'Processing', value: 'Processing' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Rejected', value: 'Rejected' },
    { label: 'Canceled', value: 'Canceled' },
  ];

  severityMap: Record<Status, 'info'|'secondary'|'warning'|'success'|'danger'|'contrast'> = {
    Requested: 'secondary',
    Initiated: 'info',
    Processing: 'warning',
    Completed: 'success',
    Rejected: 'danger',
    Canceled: 'contrast'
  };

  // Responsive table controls
  responsiveLayout: 'stack' | 'scroll' = 'scroll';
  tableMinWidth = '60rem';

  private setResponsive(width = window.innerWidth) {
    // Use stack layout on narrow screens, scroll with fixed min-width otherwise
    if (width < 640) { // < sm
      this.responsiveLayout = 'stack';
      this.tableMinWidth = '0';
    } else if (width < 1024) { // sm-md
      this.responsiveLayout = 'scroll';
      this.tableMinWidth = '48rem';
    } else {
      this.responsiveLayout = 'scroll';
      this.tableMinWidth = '60rem';
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setResponsive();
  }

  getSeverity(s: Status): 'info'|'secondary'|'warning'|'success'|'danger'|'contrast' {
    return this.severityMap[s];
  }

  ngOnInit(): void {
    this.setResponsive();
    this.fetch();
    this.searchSubject.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.page = 1;
      this.fetch();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  fetch(): void {
    this.loading.set(true);
    const params: any = { page: this.page, limit: this.limit };
    if (this.statusFilter) params.status = this.statusFilter;
    if (this.searchTerm) params.search = this.searchTerm;

    this.api.getAll(params).subscribe({
      next: (res) => {
        this.rows.set(res.data || []);
        this.total.set(res.pagination?.total || 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load withdrawals' });
        console.error(err);
      }
    });
  }

  pageChange(event: any) {
    // PrimeNG Paginator: event.page is 0-index
    this.page = (event?.page ?? 0) + 1;
    this.limit = event?.rows ?? this.limit;
    this.fetch();
  }

  onSearchInput(event: any): void {
    const value = event?.target?.value ?? '';
    this.searchTerm = value.trim();
    this.searchSubject.next(this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.page = 1;
    this.fetch();
  }

  canInitiate(s: Status) { return s === 'Requested'; }
  canProcess(s: Status) { return s === 'Initiated'; }
  canComplete(s: Status) { return s === 'Processing'; }
  canReject(s: Status) { return s === 'Requested' || s === 'Initiated' || s === 'Processing'; }

  openAction(row: WithdrawalItem, action: 'initiate'|'process'|'complete'|'reject') {
    this.activeRow.set(row);
    this.actionType.set(action);
    this.actionRef.set(row.referenceNumber || '');
    this.actionNotes.set('');
    this.showActionDialog.set(true);
  }

  confirmAction() {
    const row = this.activeRow();
    const action = this.actionType();
    if (!row || !action) return;

    const body: any = { action, referenceNumber: this.actionRef() || undefined, adminNotes: this.actionNotes() || undefined };

    // Confirm destructive actions explicitly
    if (action === 'reject' || action === 'complete') {
      this.confirm.confirm({
        message: action === 'reject'
          ? 'Reject this withdrawal? Funds on hold will be released.'
          : 'Complete this withdrawal? Funds will be debited and ROI (if any) consumed.',
        header: 'Please Confirm',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.executeAction(row._id, body),
      });
    } else {
      this.executeAction(row._id, body);
    }
  }

  private executeAction(
    id: string,
    body: { action: 'initiate'|'process'|'complete'|'reject'; referenceNumber?: string; adminNotes?: string }
  ) {
    this.loading.set(true);
    this.api.actOnWithdrawal(id, body).subscribe({
      next: () => {
        this.loading.set(false);
        this.showActionDialog.set(false);
        this.toast.add({ severity: 'success', summary: 'Success', detail: 'Withdrawal updated' });
        this.fetch(); // refresh list
      },
      error: (err) => {
        this.loading.set(false);
        this.toast.add({ severity: 'error', summary: 'Error', detail: err?.error?.error || 'Update failed' });
        console.error(err);
      }
    });
  }

  fullName(u: any): string {
    if (!u) return '-';
    if (typeof u === 'string') return u;
    return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u._id;
  }
}
